import { brandColors } from '@aegislab/design-tokens';

/** Frontend-only CSS effect tokens — box-shadow strings.
 *  Color values come from `@aegislab/design-tokens` (brand-colors). */
export const brightTokens = {
  primary: brandColors.primary,
  secondary: brandColors.secondary,
  warm: brandColors.warm,
  cool: brandColors.cool,
  rarity: brandColors.rarity,
  status: brandColors.status,
  textHighlight: brandColors.textHighlight,
  glow: {
    primary: '0 0 12px rgba(115, 167, 236, 0.3), 0 0 24px rgba(115, 167, 236, 0.18)',
    cyan: 'rgba(119, 204, 230, 0.4)',
    gold: '0 0 12px rgba(255, 215, 0, 0.3), 0 0 24px rgba(255, 215, 0, 0.18)',
    purple: '0 0 12px rgba(168, 85, 247, 0.3), 0 0 24px rgba(168, 85, 247, 0.18)',
    red: '0 0 12px rgba(212, 72, 72, 0.3), 0 0 24px rgba(212, 72, 72, 0.18)',
  },
  borderGlow: {
    default: '0 0 8px rgba(115, 167, 236, 0.2), inset 0 0 8px rgba(115, 167, 236, 0.1)',
    intense: '0 0 9px rgba(115, 167, 236, 0.36), 0 0 18px rgba(115, 167, 236, 0.18)',
    pulse: '0 0 6px rgba(119, 204, 230, 0.3)',
  },
  medal: brandColors.medal,
  overlay: brandColors.overlay,
  badge: brandColors.badge,
  card: brandColors.card,
  glowColor: brandColors.glowColor,
  agent: brandColors.agent,
  point: brandColors.point,
  // Trading Floor display board palette — phosphor LED panel 정서.
  // Plan: docs/archive/scenario-bay-improvement-plan.md §1.12 LOCK 2026-05-09.
  // 도메인: scenario detail open chrome (TradingFloorPanel). 후속 NOC alert·가챠·system status 공유 가능.
  // Tailwind palette 표준 색 — 도메인-중립 (shared/notification/gacha/quest 등 광범위 사용).
  // 기존 successGreen/errorRed/warningAmber/purple 카테고리는 다른 RGB라 별도 토큰.
  tailwind: {
    // amber-300/400/500 family
    amber300: '#fcd34d',
    amber400: '#facc15',
    amber400Wash: 'rgba(251, 191, 36, 0.08)',
    amber400Soft: 'rgba(251, 191, 36, 0.16)',
    amber400Muted: 'rgba(251, 191, 36, 0.22)',
    amber400Accent: 'rgba(251, 191, 36, 0.40)',
    amber400Strong: 'rgba(251, 191, 36, 0.90)',
    amber500Strong: 'rgba(245, 158, 11, 0.90)',
    // violet-500
    violet: '#a78bfa',
    /** violet 계열 본문 텍스트 tint — solid violet 보다 밝아 긴 문장에서 읽힌다 */
    violetTint: '#C9B8FF',
    violetWash: 'rgba(139, 92, 246, 0.08)',
    violetSoft: 'rgba(139, 92, 246, 0.12)',
    violetMuted: 'rgba(139, 92, 246, 0.18)',
    violetAccent: 'rgba(139, 92, 246, 0.30)',
    violetBold: 'rgba(139, 92, 246, 0.40)',
    violetFirm: 'rgba(139, 92, 246, 0.45)',
    violetStrong: 'rgba(139, 92, 246, 0.50)',
    // green-400/500
    green400: '#7be3a0',
    green400Soft: 'rgba(74, 222, 128, 0.16)',
    green400Accent: 'rgba(74, 222, 128, 0.30)',
    green500Bold: 'rgba(34, 197, 94, 0.65)',
    green500Strong: 'rgba(34, 197, 94, 0.70)',
    // red-500
    red500: '#ef4444',
    red500Bold: 'rgba(239, 68, 68, 0.60)',
    red500Strong: 'rgba(239, 68, 68, 0.70)',
    // pink-500
    pink500: '#ec4899',
    pink500Strong: 'rgba(236, 72, 153, 0.70)',
    // orange-400
    orange400: '#fb923c',
    // sky/blue-400
    blue400: '#93c5fd',
    blue400Soft: 'rgba(96, 165, 250, 0.16)',
    blue400Accent: 'rgba(96, 165, 250, 0.30)',
    sky400Wash: 'rgba(106, 163, 255, 0.08)',
    sky400Accent: 'rgba(106, 163, 255, 0.32)',
    // indigo-500
    indigoStrong: 'rgba(99, 102, 241, 0.70)',
  },
  // Dark navy panel scale — admin/scenario/auth 광범위 배경.
  darkNavy: {
    slate900: '#0a0e1a',
    slate900Bg: 'rgba(15, 23, 42, 0.95)',
    slate900Mid: 'rgba(15, 23, 42, 0.72)',
    slate900Soft: 'rgba(15, 23, 42, 0.60)',
    slate800: 'rgba(30, 41, 59, 0.68)',
    navy: '#0c2032',
    navyDeep: '#143042',
    navyMid: 'rgba(50, 65, 100, 0.60)',
    panel: '#1a1f2e',
    panelBg: '#0b1015',
  },
  // Light slate-blue scale — Notification/Toast/quest light variants.
  iceBlue: {
    light: '#e8f0ff',
    bone: '#c8d6e0',
    faintSoft: 'rgba(180, 220, 255, 0.70)',
    bright: 'rgba(180, 220, 255, 0.95)',
    solid: 'rgba(180, 220, 255, 1)',
    slate400Soft: 'rgba(148, 163, 184, 0.08)',
    slate400Muted: 'rgba(148, 163, 184, 0.25)',
    midIceLight: 'rgba(180, 200, 220, 0.42)',
  },
  // AdminAttendancePage PB0 — magenta cyber accent + cool cyan deep + alert/ambient.
  // [Source: project_attendance_design_direction 터미널/시스템 모티프]
  adminAttendance: {
    magenta: '#ff5fb1',
    magentaWash: 'rgba(255, 95, 177, 0.08)',
    magentaSoft: 'rgba(255, 95, 177, 0.12)',
    magentaMuted: 'rgba(255, 95, 177, 0.18)',
    magentaAccent: 'rgba(255, 95, 177, 0.30)',
    magentaBold: 'rgba(255, 95, 177, 0.50)',
    magentaDeep: '#a83a7a',
    alertWash: 'rgba(255, 71, 71, 0.04)',
    alertSoft: 'rgba(255, 71, 71, 0.08)',
    alertMuted: 'rgba(255, 71, 71, 0.10)',
    ambientMist: 'rgba(255, 181, 71, 0.06)',
    ambientWash: 'rgba(255, 181, 71, 0.08)',
    ambientSoft: 'rgba(255, 181, 71, 0.12)',
    ambientMuted: 'rgba(255, 181, 71, 0.22)',
    ambientAccent: 'rgba(255, 181, 71, 0.40)',
    ambientBold: 'rgba(255, 181, 71, 0.50)',
    cyberMid: '#3ba5b8',
    cyberDeep: '#1a4d5a',
    cyberAbyss: '#06090c',
  },
  // Scenario surface accent 색 — coral/pink/yellow/lavender 파스텔. ScenarioDashboard·QuestScenarioBrief 사용.
  scenarioAccent: {
    coral: '#fca5a5',
    coralSoft: 'rgba(248, 113, 113, 0.16)',
    coralAccent: 'rgba(248, 113, 113, 0.30)',
    coralBold: 'rgba(248, 113, 113, 0.45)',
    coralStrong: 'rgba(248, 113, 113, 0.50)',
    coralMuted: 'rgba(248, 113, 113, 0.18)',
    coralFirm: 'rgba(248, 113, 113, 0.40)',
    pink: '#f9a8d4',
    pinkSoft: 'rgba(244, 114, 182, 0.16)',
    pinkAccent: 'rgba(244, 114, 182, 0.30)',
    yellow: '#fde047',
    yellowSoft: 'rgba(250, 204, 21, 0.16)',
    yellowAccent: 'rgba(250, 204, 21, 0.30)',
    nightSoft: 'rgba(20, 25, 40, 0.50)',
    iceLavender: 'rgba(140, 165, 230, 0.14)',
  },
  // KPI 색 — dashboard/leaderboard 친화 lighter tone. 도메인-중립 (ceo-console / shared 활용).
  // 기존 successGreen/errorRed/warningAmber보다 밝아 KPI 영역 별도 토큰.
  kpi: {
    positive: '#7BD08F',
    positiveLight: '#9DD480',
    positiveSoft: 'rgba(123, 208, 143, 0.10)',
    positiveMuted: 'rgba(123, 208, 143, 0.36)',
    negative: '#FF8E8E',
    negativeLight: '#FFB3B3',
    highlight: '#E2C772',
    info: '#8EBFFF',
    infoSoft: 'rgba(140, 200, 255, 0.16)',
    infoMuted: 'rgba(140, 200, 255, 0.20)',
  },
  // AXIOM 시그니처 색 alpha scale — 도메인-중립.
  // hue: cyan(#77CCE6, Terminal cyan) / lumi(#73A7EC, Luminarium shell) / aqua(#7BE7FF, 밝은 cyan 변종)
  // tier: solid(1.0) / strong(.95) / accent(.6) / border(.3) / soft(.16) / wash(.08) / mist(.04)
  // 산재된 cyan/lumi/aqua alpha 변종을 가장 가까운 tier로 흡수 (2026-05-27 LOCK)
  signature: {
    cyanSolid: '#77CCE6',
    cyanStrong: 'rgba(119, 204, 230, 0.95)',
    cyanAccent: 'rgba(119, 204, 230, 0.6)',
    cyanBorder: 'rgba(119, 204, 230, 0.3)',
    cyanSoft: 'rgba(119, 204, 230, 0.16)',
    cyanWash: 'rgba(119, 204, 230, 0.08)',
    cyanMist: 'rgba(119, 204, 230, 0.04)',
    lumiSolid: '#73A7EC',
    lumiStrong: 'rgba(115, 167, 236, 0.95)',
    lumiAccent: 'rgba(115, 167, 236, 0.6)',
    lumiBorder: 'rgba(115, 167, 236, 0.3)',
    lumiSoft: 'rgba(115, 167, 236, 0.16)',
    lumiWash: 'rgba(115, 167, 236, 0.08)',
    lumiMist: 'rgba(115, 167, 236, 0.04)',
    aquaSolid: '#7BE7FF',
    aquaStrong: 'rgba(123, 231, 255, 0.95)',
    aquaAccent: 'rgba(123, 231, 255, 0.6)',
    aquaBorder: 'rgba(123, 231, 255, 0.3)',
    aquaSoft: 'rgba(123, 231, 255, 0.16)',
    aquaWash: 'rgba(123, 231, 255, 0.08)',
    aquaMist: 'rgba(123, 231, 255, 0.04)',
  },
  tradingFloor: {
    green: '#42FF8A',
    amber: '#FFB400',
    red: '#FF4060',
    cyan: '#77CCE6',
    white: '#E0F0FF',
    // cyan alpha 변형 — Dashboard slot 패턴(panel/border/label/faint) 공유.
    cyanStrong: 'rgba(119, 204, 230, 0.95)',
    cyanLabel: 'rgba(119, 204, 230, 0.67)',
    cyanBorder: 'rgba(119, 204, 230, 0.32)',
    cyanFaint: 'rgba(119, 204, 230, 0.18)',
    panelBg: 'rgba(0, 8, 18, 0.55)',
    // 칩 팝오버 3종이 쓰는 불투명 패널 — 0.55 는 뒤 텍스트가 비쳐 팝오버 본문이 안 읽힌다.
    panelBgOpaque: 'rgba(0, 8, 18, 0.96)',
    greenGlow: '0 0 6px rgba(66, 255, 138, 0.55), 0 0 1px rgba(66, 255, 138, 0.85)',
    amberGlow: '0 0 6px rgba(255, 180, 0, 0.55), 0 0 1px rgba(255, 180, 0, 0.85)',
    redGlow: '0 0 6px rgba(255, 64, 96, 0.55), 0 0 1px rgba(255, 64, 96, 0.85)',
    cyanGlow: '0 0 6px rgba(119, 204, 230, 0.55), 0 0 1px rgba(119, 204, 230, 0.85)',
  },
  // 월드퀘스트 아웃브레이크 긴급 사건 전용 semantic — 고채도 오렌지-amber #FFB400.
  // DESIGN.md Semantic Colors(2026-06-17): Gold(#FFD700 보상)·tailwind.amber400(#facc15 골드)와
  // 의도적 분리 — "사건 ≠ 보상"으로 읽히도록 오렌지 쪽으로 채도/색상 이동. 빨강 금지(사건≠오류).
  // '대응' CTA fill + dark text 대비 ≥4.5:1.
  outbreak: {
    base: '#FFB400',
    wash: 'rgba(255, 180, 0, 0.08)',
    soft: 'rgba(255, 180, 0, 0.16)',
    muted: 'rgba(255, 180, 0, 0.22)',
    accent: 'rgba(255, 180, 0, 0.40)',
    strong: 'rgba(255, 180, 0, 0.90)',
    // hover fill — base를 살짝 밝힌 오렌지-amber (골드로 미끄러지지 않게 유지)
    hover: '#FFC640',
    // 표면·잉크 계열 (2026-08-04 토큰화) — OutbreakPin·OutbreakTakeover 가 하드코딩으로 들고 있던 값.
    // 값은 당시 화면 그대로 옮긴 것이라 임의 조정 금지 (colorTokenContract.test.ts 가 못박음).
    panel: 'rgba(36, 28, 14, 0.95)',
    panelSoft: 'rgba(36, 28, 14, 0.65)',
    /** prefers-reduced-transparency fallback — panel 계열의 불투명 대응 */
    panelOpaque: 'rgb(36, 28, 14)',
    /** takeover 풀스크린 바닥 */
    backdrop: '#120d05',
    /** 선택된 퀘스트 행 — base(#FFB400) 알파. soft(0.16)와 다른 단계라 별도 */
    selected: 'rgba(255, 180, 0, 0.14)',
    ink: '#FBE6C0',
    inkMuted: 'rgba(251, 230, 192, 0.7)',
    inkSoft: 'rgba(251, 230, 192, 0.65)',
    inkFaint: 'rgba(251, 230, 192, 0.55)',
    /** amber fill 위 어두운 글자 — countPill·CTA */
    inkOnFill: '#1a140a',
    /** [대응] CTA hover fill. hover(#FFC640)와 다른 색이라 별도 */
    ctaHover: '#ffc933',
  },
  // 예산 이상치 tint — 글로우 없이 background tint 만으로 "정렬에서 벗어남" 표시.
  // **경보 패널 전용**(BudgetMeasurementPanel). 원장 표는 Ledger 층(무채 격자)만 쓰므로
  // 행 tint 키(overspentRow · underspentRow)는 2026-08-20 잠금 3 이행으로 제거했다.
  // red/amber 둘 다 팔레트 색(palette.red 등)과 다른 iOS 계열 톤이라 stateColor 로 흡수되지 않는다.
  ledgerAnomaly: {
    /** 과다진 — 경보 패널 */
    overspentPanel: 'rgba(255, 59, 48, 0.06)',
    /** 과다진 — 이상치 목록 항목 */
    overspentItem: 'rgba(255, 59, 48, 0.04)',
    /** 과소진 — 이상치 목록 항목 */
    underspentItem: 'rgba(255, 170, 0, 0.03)',
  },
} as const;

export type BrightTokens = typeof brightTokens;
